import { defineStore } from 'pinia';
import { ref } from 'vue';
import { StoreNames } from '../../../shared/store-names';
import { useAccountApi } from '../services/api';

type CustomDataSchema = Record<string, [{ key: string; type: 'string' | 'float' | 'boolean' | 'datetime' }]>;

export const useAccountStore = defineStore(StoreNames.Account, () => {
  const id = ref('');
  const name = ref('');
  const locale = ref('');
  const currency = ref('');
  const whReplenishmentActive = ref(false);
  const timeZone = ref('UTC');
  const customDataSchema = ref<CustomDataSchema>({});
  const accountLastReplenishmentDate = ref('');
  const calculationDoneAt = ref('');
  const promotionAiEnabled = ref(false);
  const replenishmentPickingListActive = ref(false);
  const packSupported = ref(false);
  const isRunningDailyUpload = ref(false);
  const isUpdatingReplenishments = ref(false);
  const productSimilaritySupport = ref(false);
  const searchMatchingStrategy = ref<'fuzzy' | 'exact'>('fuzzy');
  const searchBulkOperationsDisabled = ref(false);
  const itContactEmail = ref<null | string>(null);
  const zeroingEnabled = ref(false);
  const isElephant = ref(false);

  const api = useAccountApi();

  const fetching = ref(false);
  const fetched = ref(false);

  let fetchingPromise: Promise<void> | null = null;

  async function fetch(force = false) {
    if (fetched.value && !force) {
      return fetchingPromise ?? Promise.resolve();
    }

    if (!fetchingPromise) {
      fetchingPromise = (async () => {
        fetching.value = true;

        const [accountData, uploadsData] = await Promise.all([
          api.getAccountSettings(),
          api
            .getUploads({
              page: 1,
              per_page: 1,
            })
            .catch(() => []),
        ]);

        id.value = accountData.id;
        name.value = accountData.name;
        locale.value = accountData.locale;
        currency.value = accountData.currency;
        timeZone.value = accountData.timezone_name || 'UTC';
        whReplenishmentActive.value = accountData.wh_replenishment_active;
        customDataSchema.value = accountData.custom_data_schema ?? {};
        accountLastReplenishmentDate.value = accountData.account_last_replenishment_date;
        promotionAiEnabled.value = accountData.promotion_ai_enabled;
        replenishmentPickingListActive.value = accountData.replenishment_picking_list_active;
        packSupported.value = accountData.pack_supported;
        isUpdatingReplenishments.value = accountData.is_updating_replenishments;
        isRunningDailyUpload.value = accountData.is_running_daily_upload;
        calculationDoneAt.value = uploadsData[0]?.calculation_done_at || '';
        productSimilaritySupport.value = accountData.product_similarity_support;
        searchMatchingStrategy.value = accountData.search_matching_strategy;
        searchBulkOperationsDisabled.value = accountData.search_bulk_operations_disabled;
        itContactEmail.value = accountData.it_contact_email;
        zeroingEnabled.value = accountData.zeroing_enabled;
        isElephant.value = accountData.is_elephant ?? false;

        fetching.value = false;
        fetched.value = true;
        fetchingPromise = null;
      })();
    }

    return fetchingPromise;
  }

  function clear() {
    name.value = '';
    locale.value = '';
    currency.value = '';
    timeZone.value = 'UTC';
    whReplenishmentActive.value = false;
    customDataSchema.value = {};
    promotionAiEnabled.value = false;
    packSupported.value = false;
    isUpdatingReplenishments.value = false;
    productSimilaritySupport.value = false;
    zeroingEnabled.value = false;
    isElephant.value = false;
  }

  return {
    id,
    name,
    locale,
    currency,
    whReplenishmentActive,
    timeZone,
    customDataSchema,
    accountLastReplenishmentDate,
    promotionAiEnabled,
    packSupported,
    isUpdatingReplenishments,
    isRunningDailyUpload,
    productSimilaritySupport,
    fetching,
    fetch,
    fetched,
    clear,
    replenishmentPickingListActive,
    calculationDoneAt,
    searchMatchingStrategy,
    searchBulkOperationsDisabled,
    itContactEmail,
    zeroingEnabled,
    isElephant,
  };
});
